import type { PostTypeSummaryDto, RequirementLineInputDto, VehicleTypeSummaryDto } from '@pams/shared';
import { requirementColumns } from './policyPreview';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE FORCE GRID. ONE TRANSPOSE, AND THE REMAINING ROW.
 *
 *  The wire shape is ROW-MAJOR — one `requirementLine` per post type, with its allocation
 *  sources beneath it — because that is how PA_REQUIREMENTS §4.1 draws it and where P4
 *  attaches a fulfilment status and P5 attaches assignments. The screen is COLUMN-MAJOR:
 *  post types across the top, allocation sources down the side, because that is the table
 *  this department already keeps in a spreadsheet.
 *
 *  Both shapes are correct for their job and neither is correct for the other's. So the
 *  transpose happens HERE, once, and it is unit-tested round-trip. A second transpose
 *  written inline in a component is how the grid and the payload come to disagree about
 *  which division was asked for four men.
 *
 *  ─── ⚠️ A COLUMN'S IDENTITY IS ITS LINE'S POSITION, NOT AN ID ────────────────────
 *
 *  The obvious move is to key columns by `postTypeId`. It does not work, and the reason is
 *  FR-5.2.6c: the CP may add a ONE-OFF post row that exists only on this arrangement and
 *  has no id at all, only a free-text `adHocLabel`. Keying on the line's index costs
 *  nothing, works identically for all three kinds of column, and means `requirementColumns`
 *  in `policyPreview.ts` keeps the exact signature it has — which is what structurally
 *  prevents a command role from ever becoming a column.
 *
 *  ─── ⚠️ NOTHING IN THIS FILE KNOWS COMMAND EXISTS ───────────────────────────────
 *
 *  No parameter, no field, no branch. FR-5.2.6b (closing OD-9) puts command entirely
 *  outside requirement, allocation, fulfilment and shortfall, and the way that is held is
 *  by there being no argument through which one could arrive.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type GridColumnKind = 'post' | 'vehicle' | 'adhoc';

export type GridColumn = {
  /** ⚠️ THE IDENTITY. An index into `requirementLines`, stable for one render pass. */
  lineIndex: number;
  kind: GridColumnKind;
  /** What fits in a header cell. */
  label: string;
  /** The unabbreviated name, for a `title=`. */
  fullLabel: string;
  /**
   * ⚠️ [amended C1c §4a] WHICH POSTS MAY FILL A ONE-OFF COLUMN. EMPTY IS ANY.
   *
   * Carried on the column so the header's settings control can render its current state
   * without reaching back into `requirementLines` — the same reason `label` is here. It is
   * always empty for a `post` or `vehicle` column: those have a master answering for them, and
   * a second answer beside it would be two rules for one question.
   */
  restrictToPostTypeIds: readonly string[];
};

export type GridSource = {
  /** `<divisionId>:<unitId|>`. Stable across a re-render, and what React keys on. */
  key: string;
  divisionOrgUnitId: string;
  /** ⚠️ `null` means "any unit under this division" — the fast path, not an omission. */
  unitOrgUnitId: string | null;
};

export type ForceGrid = {
  /** Every column, in the order the lines are stored. */
  columns: GridColumn[];
  /** The personnel block. */
  postColumns: GridColumn[];
  /** ⚠️ A SEPARATE BLOCK, never mixed into the personnel columns. */
  vehicleColumns: GridColumn[];
  sources: GridSource[];
  /** TOTAL REQUIRED, by column. Indexed by `GridColumn.lineIndex`. */
  totals: number[];
  /** `cells[sourceIndex][lineIndex]`. Dense — an unallocated cell is 0, never a hole. */
  cells: number[][];
};

const sourceKey = (divisionOrgUnitId: string, unitOrgUnitId: string | null): string =>
  `${divisionOrgUnitId}:${unitOrgUnitId ?? ''}`;

/**
 * Row-major storage → the grid on screen.
 *
 * The source rows are the DISTINCT `(division, unit)` pairs across every line, in the
 * order they are first mentioned — so adding a division to one post type makes one new row
 * across the whole grid, which is what an operator expects and what "Split equally" needs.
 */
export const toGrid = (
  lines: readonly RequirementLineInputDto[],
  refs: {
    postTypes: ReadonlyMap<string, PostTypeSummaryDto>;
    vehicleTypes: ReadonlyMap<string, VehicleTypeSummaryDto>;
  },
): ForceGrid => {
  /*
   * ⚠️ LABELS COME FROM `requirementColumns` AND NOWHERE ELSE. The policy builder's live
   * preview and this grid have to agree about what a column is called, or the preview
   * stops being a preview. Ad-hoc rows are labelled here because the master knows nothing
   * about them — they are the one kind that has no id to resolve.
   */
  const resolved = requirementColumns({
    postTypeIds: lines.flatMap((line) => (line.postTypeId ? [line.postTypeId] : [])),
    postTypes: refs.postTypes,
    vehicleTypeIds: lines.flatMap((line) => (line.vehicleTypeId ? [line.vehicleTypeId] : [])),
    vehicleTypes: refs.vehicleTypes,
  });
  const byId = new Map(resolved.map((column) => [column.id, column]));

  const columns: GridColumn[] = lines.map((line, lineIndex) => {
    if (line.adHocLabel) {
      return {
        lineIndex,
        kind: 'adhoc',
        label: line.adHocLabel,
        fullLabel: `${line.adHocLabel} (this order only)`,
        restrictToPostTypeIds: line.restrictToPostTypeIds ?? [],
      };
    }
    const id = line.postTypeId ?? line.vehicleTypeId ?? '';
    const found = byId.get(id);
    /*
     * ⚠️ AN UNRESOLVED ID KEEPS ITS COLUMN HERE, unlike in `RequirementPreview` where it is
     * dropped. There the grid holds no data, so one fewer column reads as what it is;
     * here the column holds NUMBERS AN OPERATOR TYPED, and dropping it would silently
     * discard them and desynchronise every index below.
     */
    if (!found) {
      return {
        lineIndex,
        kind: line.vehicleTypeId ? 'vehicle' : 'post',
        label: '—',
        fullLabel: 'No longer available',
        restrictToPostTypeIds: [],
      };
    }
    return {
      lineIndex,
      kind: found.kind,
      label: found.label,
      fullLabel: found.fullLabel,
      restrictToPostTypeIds: [],
    };
  });

  const sources: GridSource[] = [];
  const seen = new Set<string>();
  for (const line of lines) {
    for (const allocation of line.allocations) {
      const unit = allocation.unitOrgUnitId ?? null;
      const key = sourceKey(allocation.divisionOrgUnitId, unit);
      if (seen.has(key)) continue;
      seen.add(key);
      sources.push({ key, divisionOrgUnitId: allocation.divisionOrgUnitId, unitOrgUnitId: unit });
    }
  }

  const cells = sources.map((source) =>
    lines.map((line) => {
      const match = line.allocations.find(
        (allocation) => sourceKey(allocation.divisionOrgUnitId, allocation.unitOrgUnitId ?? null) === source.key,
      );
      return match?.count ?? 0;
    }),
  );

  return {
    columns,
    postColumns: columns.filter((column) => column.kind !== 'vehicle'),
    vehicleColumns: columns.filter((column) => column.kind === 'vehicle'),
    sources,
    totals: lines.map((line) => line.requiredCount),
    cells,
  };
};

/**
 * The grid on screen → row-major storage.
 *
 * ⚠️ EVERY SOURCE ROW SURVIVES, EVEN AT ZERO — AND THAT IS LOAD-BEARING.
 *
 * `toGrid` rebuilds the source rows FROM the allocations, so a source that writes no
 * allocation does not exist on the next render. Dropping zeros here therefore deleted the
 * row the operator was working in: "+ Add source" appeared to do nothing at all (a new row
 * is all zeros), and clearing a cell to retype it took the whole division with it, which
 * read as the numbers resetting themselves.
 *
 * So the EDITING shape keeps every row, and the zeros are stripped once, at submit, by
 * `submittableLines`. "North sends nothing" and "North was never asked" are still the same
 * fact on the wire — they are just not the same thing while somebody is typing.
 */
export const fromGrid = (grid: ForceGrid, lines: readonly RequirementLineInputDto[]): RequirementLineInputDto[] =>
  lines.map((line, lineIndex) => ({
    ...line,
    requiredCount: grid.totals[lineIndex] ?? 0,
    allocations: grid.sources.map((source, sourceIndex) => ({
      divisionOrgUnitId: source.divisionOrgUnitId,
      unitOrgUnitId: source.unitOrgUnitId,
      count: grid.cells[sourceIndex]?.[lineIndex] ?? 0,
    })),
  }));

/**
 * What actually goes on the wire.
 *
 * ⚠️ THE TWO KINDS OF ROW THAT MUST NOT BE SENT: a source with no division chosen yet (the
 * row exists because the operator pressed "Add source" and has not picked one — an empty
 * string is not an ObjectId and the server would 422), and a source allocating nothing to
 * this post type (which would hand P4 an allocation task for nobody).
 *
 * Stripping happens HERE rather than in `fromGrid` so that editing state and wire state can
 * differ, which is the whole reason the grid stopped eating its own rows.
 */
export const submittableLines = (lines: readonly RequirementLineInputDto[]): RequirementLineInputDto[] =>
  lines.map((line) => ({
    ...line,
    allocations: line.allocations.filter(
      (allocation) => allocation.count > 0 && allocation.divisionOrgUnitId.trim().length > 0,
    ),
  }));

/**
 * ⚠️ THE REMAINING ROW — THE ENTIRE INTERACTION MODEL OF THIS SCREEN.
 *
 * Per column: what is still to be found. Positive means short, zero means done, negative
 * means over-allocated. There is no validation modal and no error toast anywhere near it —
 * the operator types numbers until the row goes green, and that is the whole instruction
 * anyone should ever need.
 *
 * Column-wise `total − Σ allocated`, and nothing else. It never sees a command officer,
 * because nothing in this file can.
 */
export const remainingByColumn = (grid: ForceGrid): number[] =>
  grid.totals.map((total, lineIndex) => {
    let allocated = 0;
    for (const row of grid.cells) allocated += row[lineIndex] ?? 0;
    return total - allocated;
  });

export type BalanceTone = 'balanced' | 'short' | 'over' | 'empty';

/**
 * What one remaining cell MEANS, so the component renders a tone rather than deciding one.
 *
 * ⚠️ A COLUMN WITH NO TOTAL YET IS `empty`, NOT `balanced`. Both compute to zero
 * remaining, and colouring an untouched column green would tell an operator their work
 * was finished before they had started it — the one failure this row cannot afford.
 */
export const balanceTone = (total: number, remaining: number): BalanceTone => {
  if (total === 0) return 'empty';
  if (remaining === 0) return 'balanced';
  return remaining < 0 ? 'over' : 'short';
};

/**
 * "Split equally" — one action, every column, all the selected divisions.
 *
 * ⚠️ ACROSS ALL COLUMNS AT ONCE, NOT ONE COLUMN PER CLICK. Four post types and three
 * divisions is four clicks the other way, and exit criterion 1's budget does not have
 * four clicks to spare for arithmetic a computer can do.
 *
 * ⚠️ THE REMAINDER GOES TO THE EARLIEST ROWS. 11 across three divisions is 4/4/3, never
 * 3/3/3 with two men unaccounted for — a split that does not add up to the total leaves
 * the remaining row red and the operator wondering what they did wrong.
 *
 * It OVERWRITES. It is an explicit action on a grid the operator is looking at, and a
 * version that skipped non-empty cells would do nothing at all the second time it is
 * pressed, which reads as broken.
 */
export const splitEqually = (grid: ForceGrid): ForceGrid => {
  const rowCount = grid.sources.length;
  if (rowCount === 0) return grid;

  const cells = grid.sources.map((_source, sourceIndex) =>
    grid.totals.map((total) => {
      const base = Math.floor(total / rowCount);
      const remainder = total % rowCount;
      return base + (sourceIndex < remainder ? 1 : 0);
    }),
  );

  return { ...grid, cells };
};

/**
 * ⚠️ PASTE FROM EXCEL. The department keeps these tables in spreadsheets TODAY, and
 * supporting one paste is worth more than any other single affordance on this screen.
 *
 * A clipboard from Excel, Sheets or LibreOffice is tab-separated, `\r\n`-terminated, and
 * quotes any cell containing a tab, a newline or a quote. Numbers arrive formatted:
 * `1,200` for a thousand, `—` or `-` for nothing, and a trailing empty line from the
 * final row's terminator.
 *
 * Returns `null` for a cell that is not a number, meaning LEAVE THAT CELL ALONE. Pasting a
 * block whose first column is post-type names must not zero the numbers beside them, and
 * an operator who pastes a header row by accident should see their headers ignored rather
 * than their grid wiped.
 */
export const parseClipboardMatrix = (text: string): (number | null)[][] => {
  const rows: (number | null)[][] = [];
  let row: (number | null)[] = [];
  let cell = '';
  let quoted = false;

  const pushCell = (): void => {
    row.push(toCount(cell));
    cell = '';
  };
  const pushRow = (): void => {
    pushCell();
    rows.push(row);
    row = [];
  };

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];

    if (quoted) {
      if (character === '"') {
        // A doubled quote inside a quoted cell is one literal quote.
        if (text[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += character;
      }
      continue;
    }

    if (character === '"' && cell === '') {
      quoted = true;
    } else if (character === '\t') {
      pushCell();
    } else if (character === '\n') {
      pushRow();
    } else if (character !== '\r') {
      cell += character;
    }
  }

  // The final row only exists if the text did not end on its terminator.
  if (cell !== '' || row.length > 0) pushRow();

  return rows;
};

/** `'1,200'` → 1200 · `'—'` → 0 · `'SI/Sgt'` → null, meaning "not a number, leave it". */
const toCount = (raw: string): number | null => {
  const trimmed = raw.trim();
  // An em-dash, an en-dash or a hyphen is how a spreadsheet writes "none".
  if (trimmed === '' || trimmed === '—' || trimmed === '–' || trimmed === '-') return 0;

  const cleaned = trimmed.replace(/[\s,]/g, '');
  if (!/^\d+$/.test(cleaned)) return null;

  const value = Number(cleaned);
  return Number.isSafeInteger(value) ? value : null;
};

export type PasteResult = {
  grid: ForceGrid;
  /** Cells written. What the announcement reports. */
  applied: number;
  /** Cells the block carried that fell outside the grid, so the user is told. */
  clipped: number;
};

/**
 * Lay a parsed block onto the grid from one anchor cell.
 *
 * ⚠️ A BLOCK LARGER THAN THE GRID IS CLIPPED AND THE USER IS TOLD, never silently
 * truncated and never allowed to grow the grid. Growing it would invent allocation rows
 * for divisions nobody chose, and inventing a division is worse than dropping a number.
 *
 * `anchorRow === -1` addresses the TOTAL REQUIRED row, which is where a spreadsheet block
 * is usually pasted first.
 */
export const applyPaste = (
  grid: ForceGrid,
  anchor: { row: number; column: number },
  matrix: readonly (number | null)[][],
): PasteResult => {
  const totals = [...grid.totals];
  const cells = grid.cells.map((row) => [...row]);
  let applied = 0;
  let clipped = 0;

  matrix.forEach((values, rowOffset) => {
    const targetRow = anchor.row + rowOffset;
    values.forEach((value, columnOffset) => {
      const targetColumn = anchor.column + columnOffset;
      if (value === null) return;

      if (targetColumn >= grid.totals.length || targetRow >= grid.sources.length) {
        clipped += 1;
        return;
      }

      if (targetRow < 0) {
        totals[targetColumn] = value;
      } else {
        const row = cells[targetRow];
        if (!row) {
          clipped += 1;
          return;
        }
        row[targetColumn] = value;
      }
      applied += 1;
    });
  });

  return { grid: { ...grid, totals, cells }, applied, clipped };
};
